import { useMemo, useState } from 'react';
import {
  buildDirectionsEmbedUrl,
  buildDirectionsUrl,
  getZoneInfo,
  STORE_LABEL,
} from '../../utils/routeUtils';

interface DeliveryMapProps {
  destination: string;
  compact?: boolean;
  className?: string;
}

const ZONE_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  green:  { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-100', dot: 'bg-green-500'  },
  orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-100', dot: 'bg-orange-500' },
  yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-100', dot: 'bg-yellow-500' },
};

export default function DeliveryMap({ destination, compact = false, className = '' }: DeliveryMapProps) {
  const [loaded, setLoaded] = useState(false);

  const embedUrl = useMemo(() => buildDirectionsEmbedUrl(destination), [destination]);
  const mapsUrl  = useMemo(() => buildDirectionsUrl(destination),      [destination]);
  const info     = useMemo(() => getZoneInfo(destination),             [destination]);

  const mapHeight = compact ? 'h-44' : 'h-64';
  const c = ZONE_COLORS[info.zone.color] ?? ZONE_COLORS.orange;

  return (
    <div className={`rounded-2xl overflow-hidden border border-gray-100 bg-white ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#FF6B35]/10 text-[#FF6B35]">
            <i className="ri-map-2-line text-sm" />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-800">Delivery Route</p>
            <p className="text-[10px] text-gray-400 truncate max-w-[180px]">
              {STORE_LABEL} → {destination}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* ETA badge */}
          <div className={`${c.bg} border ${c.border} rounded-lg px-2.5 py-1 text-center`}>
            <p className={`text-[9px] ${c.text} font-semibold uppercase tracking-wide`}>ETA</p>
            <p className={`text-sm font-black ${c.text} leading-tight`}>{info.eta} min</p>
          </div>

          <a
            href={mapsUrl}
            target="_blank"
            rel="nofollow noreferrer"
            title="Open in Google Maps"
            className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition cursor-pointer"
          >
            <i className="ri-external-link-line text-xs text-gray-500" />
          </a>
        </div>
      </div>

      {/* Zone pill */}
      <div className={`flex items-center gap-1.5 px-4 py-1.5 ${c.bg} border-b ${c.border}`}>
        <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${c.dot}`} />
        <p className={`text-[10px] font-semibold ${c.text}`}>{info.zone.label}</p>
        <span className={`text-[10px] ${c.text} opacity-70`}>· {info.etaRange}</span>
      </div>

      {/* Map iframe */}
      <div className={`relative w-full ${mapHeight} bg-gray-100`}>
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-gray-400">
            <div className="w-5 h-5 flex items-center justify-center animate-spin">
              <i className="ri-loader-4-line text-base text-[#FF6B35]" />
            </div>
            <span className="text-xs">Loading map…</span>
          </div>
        )}
        <iframe
          title="Delivery route map"
          src={embedUrl}
          className={`w-full h-full border-0 transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          allowFullScreen
        />
      </div>

      {/* Footer */}
      <div className="px-4 py-2 flex items-center justify-between bg-[#FAFAF8]">
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
          <i className="ri-motorbike-line text-xs text-[#FF6B35]" />
          <span>From Providence, EBD · Via Google Maps</span>
        </div>
        <a
          href={mapsUrl}
          target="_blank"
          rel="nofollow noreferrer"
          className="text-[10px] text-[#FF6B35] font-semibold hover:underline cursor-pointer whitespace-nowrap"
        >
          Open Maps →
        </a>
      </div>
    </div>
  );
}
